import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { userAPI } from '../services/api';
import Layout from '../components/Layout';
import ProfileImageUpload from '../components/ProfileImageUpload';
import PhoneInput, { isValidPhoneNumber, getCountries, getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input';
import Input from 'react-phone-number-input/input';
import en from 'react-phone-number-input/locale/en';
import 'react-phone-number-input/style.css';
import { toast } from 'sonner';
import './Team.css';

const EditTeamMember = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    mobile: '',
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',
    nationality: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelation: '',
    role: 'Developer',
    department: '',
    employeeId: '',
    joiningDate: new Date().toISOString().split('T')[0],
    employmentType: 'Full-time',
    workLocation: 'Office',
    designation: '',
    salary: '',
    reportsTo: '',
    education: '',
    university: '',
    graduationYear: '',
    experience: '',
    previousCompany: '',
    skills: '',
    profileImage: '',
    isActive: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [selectedMobileCountry, setSelectedMobileCountry] = useState('IN');
  const [errors, setErrors] = useState({});

  const roles = [
    'HR',
    'Operations Head',
    'Project Manager',
    'Project Lead',
    'Developer',
    'QA Engineer',
    'UI Engineer'
  ];

  useEffect(() => {
    loadUsers();
    loadMember();
  }, [id]);

  const loadUsers = async () => {
    try {
      const response = await userAPI.getAll();
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadMember = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getById(id);
      const member = response.data.data;

      console.log('Loaded member data:', member);

      setFormData({
        name: member.name,
        username: member.username || '',
        email: member.email,
        mobile: member.mobile,
        dateOfBirth: member.dateOfBirth ? member.dateOfBirth.split('T')[0] : '',
        gender: member.gender || '',
        maritalStatus: member.maritalStatus || '',
        nationality: member.nationality || '',
        address: member.address || '',
        city: member.city || '',
        state: member.state || '',
        zipCode: member.zipCode || '',
        country: member.country || '',
        emergencyContactName: member.emergencyContactName || '',
        emergencyContactPhone: member.emergencyContactPhone || '',
        emergencyContactRelation: member.emergencyContactRelation || '',
        role: member.role,
        department: member.department || '',
        employeeId: member.employeeId || '',
        joiningDate: member.joiningDate ? member.joiningDate.split('T')[0] : new Date().toISOString().split('T')[0],
        employmentType: member.employmentType || 'Full-time',
        workLocation: member.workLocation || 'Office',
        designation: member.designation || '',
        salary: member.salary || '',
        reportsTo: member.reportsTo?._id || '',
        education: member.education || '',
        university: member.university || '',
        graduationYear: member.graduationYear || '',
        experience: member.experience || '',
        previousCompany: member.previousCompany || '',
        skills: member.skills || '',
        profileImage: member.profileImage || '',
        isActive: member.isActive !== undefined ? member.isActive : true
      });

      if (member.mobile) {
        try {
          const parsed = parsePhoneNumber(member.mobile);
          if (parsed) {
            setSelectedMobileCountry(parsed.country);
          }
        } catch (e) {
          console.warn('Failed to parse mobile number:', member.mobile);
        }
      }
    } catch (error) {
      console.error('Failed to load member:', error);
      alert('Failed to load team member details');
      navigate('/team');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = async (file, preview) => {
    setProfileImageFile(file);
    if (file) {
      try {
        setUploadingImage(true);
        const response = await userAPI.uploadProfileImage(file);
        if (response.data.success && response.data.imageUrl) {
          setFormData(prev => ({ ...prev, profileImage: response.data.imageUrl }));
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        console.error('Image upload error:', error);
        alert('Failed to upload image: ' + (error.response?.data?.message || error.message));
        setFormData(prev => ({ ...prev, profileImage: prev.profileImage }));
      } finally {
        setUploadingImage(false);
      }
    } else {
      setFormData(prev => ({ ...prev, profileImage: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Full name is required';
    }

    if (!formData.mobile) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!isValidPhoneNumber(formData.mobile)) {
      newErrors.mobile = 'Please enter a valid mobile number for the selected country';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        mobile: formData.mobile.trim()
      };

      await userAPI.update(id, submitData);
      toast.success('Team member updated successfully');
      navigate('/team');
    } catch (error) {
      console.error('Update member error:', error);
      toast.error(error.response?.data?.message || 'Failed to update team member');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="team-page">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading member details...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="team-page">
        <div className="page-header">
          <div>
            <h1 className="page-title">Edit Employee</h1>
            <p className="page-subtitle">Update team member information</p>
          </div>
          <button className="btn-back" onClick={() => navigate('/team')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back to Team
          </button>
        </div>

        <div className="form-container">
          <form onSubmit={handleSubmit} className="team-form">
            <div className="form-section">
              <h3 className="section-title">Employee Information</h3>
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={submitting}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Username *</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={submitting}
                    required
                    autoComplete="username"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    className="form-input"
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Mobile Number *</label>
                  <div className={`premium-phone-input ${errors.mobile ? 'has-error' : ''}`}>
                    <div className="country-select-wrapper">
                      {selectedMobileCountry ? (
                        <>
                          <img
                            src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${selectedMobileCountry}.svg`}
                            alt={selectedMobileCountry}
                            className="flag-icon"
                          />
                          <span className="calling-code">+{getCountryCallingCode(selectedMobileCountry)}</span>
                        </>
                      ) : (
                        <div className="globe-icon">🌐</div>
                      )}
                      <select
                        className="country-native-select"
                        value={selectedMobileCountry || ''}
                        onChange={(e) => {
                          const newCountry = e.target.value || null;
                          setSelectedMobileCountry(newCountry);
                          if (newCountry) {
                            setErrors(prev => ({ ...prev, mobile: '' }));
                          }
                        }}
                        disabled={submitting}
                      >
                        <option value="">Select Country</option>
                        {getCountries().map((country) => (
                          <option key={country} value={country}>
                            {en[country]} (+{getCountryCallingCode(country)})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="phone-input-field-wrapper">
                      <Input
                        country={selectedMobileCountry}
                        placeholder={selectedMobileCountry ? `Enter number for ${en[selectedMobileCountry]}` : "Select country first"}
                        value={formData.mobile}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, mobile: value || '' }));

                          // Real-time validation
                          if (value) {
                            if (isValidPhoneNumber(value)) {
                              setErrors(prev => ({ ...prev, mobile: '' }));
                            } else {
                              setErrors(prev => ({ ...prev, mobile: 'Please enter a valid mobile number for the selected country' }));
                            }
                          } else {
                            setErrors(prev => ({ ...prev, mobile: '' }));
                          }
                        }}
                        disabled={!selectedMobileCountry || submitting}
                        className="PhoneInputInput"
                      />
                    </div>
                  </div>
                  {errors.mobile && <span className="error-text">{errors.mobile}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Date of Birth</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={submitting}
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Gender *</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={submitting}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Marital Status</label>
                  <select
                    name="maritalStatus"
                    value={formData.maritalStatus}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={submitting}
                  >
                    <option value="">Select Status</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Nationality</label>
                  <input
                    type="text"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleInputChange}
                    placeholder="American"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    name="isActive"
                    value={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'true' })}
                    className="form-select"
                    disabled={submitting}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title">Address Details</h3>
              <div className="form-group">
                <label className="form-label">Street Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="123 Main Street"
                  className="form-input"
                  disabled={submitting}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="New York"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">State/Province</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="NY"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Zip/Postal Code</label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    placeholder="10001"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="United States"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title">Emergency Contact</h3>
              <div className="form-group">
                <label className="form-label">Contact Name</label>
                <input
                  type="text"
                  name="emergencyContactName"
                  value={formData.emergencyContactName}
                  onChange={handleInputChange}
                  placeholder="Jane Doe"
                  className="form-input"
                  disabled={submitting}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Contact Phone</label>
                  <input
                    type="tel"
                    name="emergencyContactPhone"
                    value={formData.emergencyContactPhone}
                    onChange={handleInputChange}
                    placeholder="+1234567890"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Relationship</label>
                  <select
                    name="emergencyContactRelation"
                    value={formData.emergencyContactRelation}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={submitting}
                  >
                    <option value="">Select Relationship</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Parent">Parent</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Child">Child</option>
                    <option value="Friend">Friend</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title">Employment Details</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Role *</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={submitting}
                    required
                  >
                    {roles.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Department</label>
                  <select
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={submitting}
                  >
                    <option value="">Select Department</option>
                    <option value="Engineering">Engineering</option>
                    <option value="Quality Assurance">Quality Assurance</option>
                    <option value="Design">Design</option>
                    <option value="Product Management">Product Management</option>
                    <option value="Human Resources">Human Resources</option>
                    <option value="Operations">Operations</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Employee ID</label>
                  <input
                    type="text"
                    name="employeeId"
                    value={formData.employeeId}
                    onChange={handleInputChange}
                    placeholder="EMP001"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Designation</label>
                  <input
                    type="text"
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    placeholder="Senior Developer"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Joining Date *</label>
                  <input
                    type="date"
                    name="joiningDate"
                    value={formData.joiningDate}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={submitting}
                    max="9999-12-31"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Employment Type</label>
                  <select
                    name="employmentType"
                    value={formData.employmentType}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={submitting}
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Intern">Intern</option>
                    <option value="Consultant">Consultant</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Work Location</label>
                  <select
                    name="workLocation"
                    value={formData.workLocation}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={submitting}
                  >
                    <option value="Office">Office</option>
                    <option value="Remote">Remote</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Annual Salary</label>
                  <input
                    type="number"
                    name="salary"
                    value={formData.salary}
                    onChange={handleInputChange}
                    placeholder="50000"
                    className="form-input"
                    disabled={submitting}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Reports To</label>
                <select
                  name="reportsTo"
                  value={formData.reportsTo}
                  onChange={handleInputChange}
                  className="form-select"
                  disabled={submitting}
                >
                  <option value="">None</option>
                  {users.filter(u =>
                    u.role === 'Project Lead' ||
                    u.role === 'Project Manager' ||
                    u.role === 'Operations Head'
                  ).map(u => (
                    <option key={u._id} value={u._id}>{u.name} ({u.role})</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-section">
              <h3 className="section-title">Education & Experience</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Highest Education</label>
                  <select
                    name="education"
                    value={formData.education}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={submitting}
                  >
                    <option value="">Select Education</option>
                    <option value="High School">High School</option>
                    <option value="Associate Degree">Associate Degree</option>
                    <option value="Bachelor's Degree">Bachelor's Degree</option>
                    <option value="Master's Degree">Master's Degree</option>
                    <option value="Doctorate">Doctorate</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">University/College</label>
                  <input
                    type="text"
                    name="university"
                    value={formData.university}
                    onChange={handleInputChange}
                    placeholder="MIT"
                    className="form-input"
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Graduation Year</label>
                  <input
                    type="number"
                    name="graduationYear"
                    value={formData.graduationYear}
                    onChange={handleInputChange}
                    placeholder="2020"
                    className="form-input"
                    disabled={submitting}
                    min="1950"
                    max={new Date().getFullYear()}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Years of Experience</label>
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={submitting}
                  >
                    <option value="">Select Experience</option>
                    <option value="Fresher">Fresher</option>
                    <option value="0 – 6 Months">0 – 6 Months</option>
                    <option value="6 Months – 1 Year">6 Months – 1 Year</option>
                    <option value="1 – 3 Years">1 – 3 Years</option>
                    <option value="3 – 5 Years">3 – 5 Years</option>
                    <option value="5 – 8 Years">5 – 8 Years</option>
                    <option value="8+ Years">8+ Years</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Previous Company</label>
                <input
                  type="text"
                  name="previousCompany"
                  value={formData.previousCompany}
                  onChange={handleInputChange}
                  placeholder="Google Inc."
                  className="form-input"
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Skills (comma separated)</label>
                <input
                  type="text"
                  name="skills"
                  value={formData.skills}
                  onChange={handleInputChange}
                  placeholder="React, Node.js, MongoDB, AWS"
                  className="form-input"
                  disabled={submitting}
                />
              </div>

              <ProfileImageUpload
                currentImage={formData.profileImage}
                onImageChange={handleImageChange}
                name={formData.name}
                disabled={submitting || uploadingImage}
              />
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => navigate('/team')} disabled={submitting}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                Cancel
              </button>
              <button type="submit" className="btn-submit" disabled={submitting}>
                {submitting ? 'Updating...' : 'Update Employee'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default EditTeamMember;